import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, formatDate, formatDuration } from "@/lib/api";
import { formatStoredWeight } from "@/components/app/WeightInput";
import { formatStoredDistance, formatStoredHeight } from "@/lib/units";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash, MagnifyingGlass, Barbell, Lightning, Trophy, Timer } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatSplit } from "@/lib/intervals";
import BackButton from "@/components/app/BackButton";
import EditLogDialog from "@/components/app/EditLogDialog";

const fmtTime = formatDuration;

// Build a one-line headline that consolidates repeated-prescription sets.
// Empty string when there's nothing useful to show (mixed sets, no weights).
function buildMovementHeadline(sets) {
  if (!sets || sets.length === 0) return "";
  const unit = sets.find((s) => s.weight_unit)?.weight_unit || "lb";
  // Case 0: any log carries the block's rep scheme (e.g. 21-15-9 ladder). The
  // prescription is the source of truth even if only 1 set was logged.
  const schemeLog = sets.find((s) => Array.isArray(s.block_rep_scheme) && s.block_rep_scheme.length);
  if (schemeLog) {
    const schemeStr = schemeLog.block_rep_scheme.join("-");
    const weights = sets.map((s) => s.weight).filter((w) => w != null);
    const allSameWeight = weights.length === sets.length && new Set(weights).size === 1;
    const w = allSameWeight && weights[0]
      ? ` @ ${formatStoredWeight(weights[0], unit)}`
      : "";
    return `${schemeStr} reps${w}`;
  }
  if (sets.length < 2) return "";
  const reps = sets.map((s) => s.reps).filter((r) => r != null);
  const weights = sets.map((s) => s.weight).filter((w) => w != null);
  const allSameReps = reps.length === sets.length && new Set(reps).size === 1;
  const allSameWeight = weights.length === sets.length && new Set(weights).size === 1;
  // Case 1: every set identical → "5 × 5 @ 135 lb"
  if (allSameReps && allSameWeight && reps.length > 0) {
    const w = weights[0] ? ` @ ${formatStoredWeight(weights[0], unit)}` : "";
    return `${sets.length} × ${reps[0]}${w}`;
  }
  // Case 2: reps identical, weights ramp → "5 sets · 135-145-155 lb · 5 reps each"
  if (allSameReps && weights.length === sets.length) {
    const wList = weights.map((w) => (Number.isInteger(w) ? w : Number(w).toFixed(1).replace(/\.0$/, "")));
    return `${sets.length} sets · ${wList.join("-")} ${unit} · ${reps[0]} reps each`;
  }
  // Case 3: weights identical, reps vary → "5 sets · 5-5-3-3-2 reps @ 135 lb"
  if (allSameWeight && reps.length === sets.length) {
    const w = weights[0] ? ` @ ${formatStoredWeight(weights[0], unit)}` : "";
    return `${sets.length} sets · ${reps.join("-")} reps${w}`;
  }
  // Case 4: both vary but everyone has both → "5 sets · 135×5, 145×5, 155×3, 165×3, 175×2"
  if (reps.length === sets.length && weights.length === sets.length) {
    const segs = sets.map((s) => `${formatStoredWeight(s.weight, s.weight_unit || unit)}×${s.reps}`);
    // Only bother showing if not absurdly long.
    if (segs.length <= 8) return `${sets.length} sets · ${segs.join(", ")}`;
  }
  return "";
}

export default function History({ athleteIdOverride }) {
  const params = useParams();
  const { user } = useAuth();
  const athleteId = athleteIdOverride || params.athleteId || user.id;
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all"); // all | pr | conditioning

  const load = async () => {
    try {
      const { data } = await api.get("/workout-logs", { params: { athlete_id: athleteId, limit: 500 } });
      setLogs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, [athleteId]);

  const filtered = logs.filter((l) => {
    if (!q) return true;
    const hay = `${l.movement_name || ""} ${l.block_title || ""} ${l.block_type || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  // Group by date → then by movement (or block).
  const grouped = filtered.reduce((acc, log) => {
    const d = (log.logged_at || "").slice(0, 10);
    acc[d] = acc[d] || [];
    acc[d].push(log);
    return acc;
  }, {});

  const del = async (id) => {
    if (!window.confirm("Delete this log entry?")) return;
    try {
      await api.delete(`/workout-logs/${id}`);
      toast.success("Deleted");
      setLogs((l) => l.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onUpdated = (updated) => {
    setLogs((l) => l.map((x) => (x.id === updated.id ? updated : x)));
  };

  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto space-y-6">
      <BackButton />
      <div>
        <div className="hs-label">Workout history</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2">Every set, remembered.</h1>
      </div>

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
        <Input
          placeholder={view === "pr" ? "Search PR by movement (e.g. back squat, deadlift)…" : view === "conditioning" ? "Search conditioning (Fran, AMRAP, EMOM, …)" : "Search by movement name…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-10 h-12 rounded-xl"
          data-testid="history-search"
        />
      </div>

      {/* View toggle */}
      <div className="flex gap-1.5" data-testid="history-view-toggle">
        <ViewTab value="all" current={view} onClick={setView} icon={Barbell} label="All logs" />
        <ViewTab value="pr" current={view} onClick={setView} icon={Trophy} label="Personal bests" />
        <ViewTab value="conditioning" current={view} onClick={setView} icon={Timer} label="Conditioning" />
      </div>

      {view === "pr" ? (
        <PRPanel logs={logs} query={q} />
      ) : view === "conditioning" ? (
        <ConditioningPanel logs={logs} query={q} onDelete={del} />
      ) : Object.keys(grouped).length === 0 ? (
        <div className="hs-card p-10 text-center text-ink-3">No logs yet.</div>
      ) : (
        <div className="space-y-6">
          {Object.keys(grouped).sort((a, b) => b.localeCompare(a)).map((d) => (
            <DayHistoryCard key={d} date={d} logs={grouped[d]} allLogs={filtered} onDelete={del} onUpdated={onUpdated} />
          ))}
        </div>
      )}
    </div>
  );
}

function ViewTab({ value, current, onClick, icon: Icon, label }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`flex-1 sm:flex-none px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-[0.08em] inline-flex items-center justify-center gap-1.5 transition-colors ${
        active ? "bg-ink text-bone" : "bg-bone text-ink-2 border border-line hover:bg-muted2"
      }`}
      data-testid={`history-view-${value}`}
    >
      <Icon size={14} weight={active ? "fill" : "regular"} />
      {label}
    </button>
  );
}

/**
 * Personal Bests panel — for every movement, surfaces the best weight at each
 * rep count (1RM, 2RM, 3RM, 5RM…). Only considers sets that have both a reps
 * count and a weight; freestyle / scheme-only logs are skipped. Sorted by
 * movement name (matches search filter).
 */
function PRPanel({ logs, query }) {
  const q = query.trim().toLowerCase();
  // movement → reps → {weight, date, weight_unit}
  const byMv = {};
  for (const l of logs) {
    if (!l.movement_name || !l.weight || !l.reps) continue;
    const mv = byMv[l.movement_name] || {};
    const cur = mv[l.reps];
    if (!cur || l.weight > cur.weight) {
      mv[l.reps] = { weight: l.weight, weight_unit: l.weight_unit || "lb", date: (l.logged_at || "").slice(0, 10) };
    }
    byMv[l.movement_name] = mv;
  }
  let names = Object.keys(byMv).sort((a, b) => a.localeCompare(b));
  if (q) names = names.filter((n) => n.toLowerCase().includes(q));
  if (names.length === 0) {
    return (
      <div className="hs-card p-10 text-center text-ink-3" data-testid="history-pr-empty">
        {q ? `No PRs match "${query}".` : "Log a few sets with reps + weight and your PRs will show up here."}
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="history-pr-panel">
      {names.map((name) => {
        const reps = Object.keys(byMv[name]).map(Number).sort((a, b) => a - b);
        return (
          <div key={name} className="hs-card p-4 sm:p-5" data-testid={`history-pr-${name}`}>
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={16} weight="fill" className="text-terracotta" />
              <h3 className="font-heading text-lg font-semibold">{name}</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {reps.map((r) => {
                const pr = byMv[name][r];
                return (
                  <div key={r} className="px-3 py-2 rounded-xl bg-sage/40">
                    <div className="hs-label text-[10px] text-moss">{r}RM</div>
                    <div className="font-data text-lg font-semibold leading-tight">
                      {formatStoredWeight(pr.weight, pr.weight_unit)}
                    </div>
                    <div className="text-[10px] text-ink-3 font-data">{formatDate(pr.date, { month: "short", day: "numeric" })}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Conditioning panel — block-level results (no per-set rows), sorted newest
 * first. Searchable by block title or block type. Each row shows the title,
 * type pill, result (time / rounds), and date.
 */
function ConditioningPanel({ logs, query, onDelete }) {
  const q = query.trim().toLowerCase();
  const COND_TYPES = ["metcon", "amrap", "emom", "tabata", "ladder", "intervals", "for_time", "chipper"];
  const rows = logs
    .filter((l) => !l.movement_name && (COND_TYPES.includes(l.block_type) || l.total_time_seconds || l.rounds_completed))
    .filter((l) => {
      if (!q) return true;
      return `${l.block_title || ""} ${l.block_type || ""}`.toLowerCase().includes(q);
    })
    .sort((a, b) => (b.logged_at || "").localeCompare(a.logged_at || ""));
  if (rows.length === 0) {
    return (
      <div className="hs-card p-10 text-center text-ink-3" data-testid="history-cond-empty">
        {q ? `No conditioning logs match "${query}".` : "Complete a section with the timer and it'll land here."}
      </div>
    );
  }
  return (
    <div className="hs-card overflow-hidden" data-testid="history-cond-panel">
      <ul className="divide-y divide-line">
        {rows.map((l) => (
          <li key={l.id} className="px-5 py-4 flex items-center gap-3" data-testid={`history-cond-row-${l.id}`}>
            <div className="h-9 w-9 rounded-lg bg-terracotta/15 grid place-items-center shrink-0">
              <Lightning size={16} weight="fill" className="text-terracotta" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-heading text-base font-semibold truncate">{l.block_title || l.block_type?.toUpperCase() || "Section"}</h4>
                {l.block_type && (
                  <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] bg-terracotta/10 text-terracotta">
                    {l.block_type}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-ink-3 font-data mt-1">
                {[
                  l.total_time_seconds ? fmtTime(l.total_time_seconds) : null,
                  l.rounds_completed != null ? `${l.rounds_completed} rds${l.extra_reps ? ` + ${l.extra_reps}` : ""}` : null,
                  l.interval_results?.length ? `${l.interval_results.length} intervals` : null,
                  l.calories ? `${l.calories} cal` : null,
                  l.distance_meters ? formatStoredDistance(l.distance_meters, l.distance_unit || "m") : null,
                  formatDate(l.logged_at),
                ].filter(Boolean).join(" · ")}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => onDelete(l.id)} className="rounded-lg text-ink-3 hover:bg-red-50 hover:text-error" data-testid={`history-cond-delete-${l.id}`}>
              <Trash size={14} />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Precompute the chronologically-prior block-completion attempt per (normalized) title.
// We pass this down so we can render a "−0:23 faster" / "+12s slower" chip on repeats.
function findPriorAttempt(allLogs, currentLog) {
  if (!currentLog?.block_id) return null;
  const titleKey = (currentLog.block_title || currentLog.block_type || "").toLowerCase().trim();
  if (!titleKey) return null;
  const cur = currentLog.logged_at || "";
  let best = null;
  for (const l of allLogs) {
    if (l.movement_name) continue; // skip per-set logs
    if (!l.block_id) continue;
    if (l.id === currentLog.id) continue;
    const k = (l.block_title || l.block_type || "").toLowerCase().trim();
    if (k !== titleKey) continue;
    if ((l.logged_at || "") >= cur) continue;
    if (!best || (l.logged_at || "") > (best.logged_at || "")) best = l;
  }
  return best;
}

// ─── Day card with movement-grouped sets ──────────────────────────

function DayHistoryCard({ date, logs, allLogs, onDelete, onUpdated }) {
  // Group day's logs by (movement_name OR block).
  // Block-completion logs (no movement_name) get their own group keyed by block.
  const groups = {};
  for (const lg of logs) {
    const isBlock = !lg.movement_name && lg.block_id;
    const key = isBlock
      ? `block::${lg.block_id}`
      : `mv::${(lg.movement_name || "Unknown").toLowerCase()}`;
    if (!groups[key]) {
      groups[key] = {
        kind: isBlock ? "block" : "movement",
        title: isBlock
          ? (lg.block_title || lg.block_type?.toUpperCase() || "Section")
          : lg.movement_name,
        block_type: lg.block_type,
        block_id: lg.block_id,
        sets: [],
      };
    }
    groups[key].sets.push(lg);
  }

  // Sort sets within each group by set_number (movement) or by logged_at (block).
  for (const g of Object.values(groups)) {
    if (g.kind === "movement") {
      g.sets.sort((a, b) => (a.set_number || 0) - (b.set_number || 0));
    } else {
      g.sets.sort((a, b) => (a.logged_at || "").localeCompare(b.logged_at || ""));
    }
  }

  const totalSets = logs.filter((l) => !!l.movement_name).length;
  const blockCount = logs.filter((l) => !l.movement_name && l.block_id).length;

  return (
    <div className="hs-card overflow-hidden" data-testid={`history-day-${date}`}>
      <div className="px-5 py-3 bg-muted2 border-b border-line flex items-center justify-between">
        <div className="font-heading font-semibold text-ink">
          {formatDate(date, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
        </div>
        <div className="hs-label text-[10px]">
          {totalSets} {totalSets === 1 ? "set" : "sets"}
          {blockCount > 0 && ` · ${blockCount} ${blockCount === 1 ? "section" : "sections"}`}
        </div>
      </div>

      <div className="divide-y divide-line">
        {Object.entries(groups).map(([key, g]) => (
          g.kind === "movement"
            ? <MovementGroup key={key} group={g} onDelete={onDelete} onUpdated={onUpdated} />
            : <BlockGroup key={key} group={g} allLogs={allLogs} onDelete={onDelete} onUpdated={onUpdated} />
        ))}
      </div>
    </div>
  );
}

function MovementGroup({ group, onDelete, onUpdated }) {
  const sets = group.sets;
  // Compute totals
  let totalVolume = 0;
  let unit = "lb";
  const summaryParts = [];
  for (const s of sets) {
    const w = s.weight || 0;
    const r = s.reps || 0;
    if (w && r) totalVolume += w * r;
    if (s.weight_unit) unit = s.weight_unit;
  }
  if (totalVolume > 0) summaryParts.push(`${Math.round(totalVolume).toLocaleString()} ${unit} total`);
  const weights = sets.map((s) => s.weight).filter(Boolean);
  if (weights.length > 1) {
    const min = Math.min(...weights);
    const max = Math.max(...weights);
    if (min !== max) summaryParts.push(`${formatStoredWeight(min, unit)} → ${formatStoredWeight(max, unit)}`);
  }

  // ── Headline consolidation ────────────────────────────────────────
  // Compress repeated-prescription sets into a short "N × R @ W" line.
  // 3 cases worth surfacing:
  //   (1) Every set identical (5 sets, 5 reps, 135 lb each)  → "5 × 5 @ 135 lb"
  //   (2) Reps identical, weights ramp                       → "5 sets · 135-145-155-165-175 lb · 5 reps each"
  //   (3) Reps & weights both vary                           → no headline (per-set rows below tell the story)
  const headline = buildMovementHeadline(sets);

  return (
    <div className="px-5 py-4" data-testid={`history-movement-${group.title}`}>
      <div className="flex items-start gap-2.5">
        <div className="h-8 w-8 rounded-lg bg-sage/70 grid place-items-center shrink-0">
          <Barbell size={16} className="text-moss" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-heading text-base font-semibold uppercase tracking-[0.04em] truncate">{group.title}</h4>
          <div className="hs-label text-[10px] mt-1">Sets</div>
        </div>
      </div>

      <ul className="mt-2 ml-10 space-y-1.5 font-data text-sm">
        {headline && (
          <li
            className="flex items-center gap-2 -mt-1 mb-1"
            data-testid={`history-headline-${group.title}`}
          >
            <span className="px-2.5 py-1 rounded-lg bg-terracotta text-white font-semibold text-xs">
              {headline}
            </span>
            <span className="hs-label text-[9px] text-ink-3">{sets.length} {sets.length === 1 ? "set" : "sets"} below</span>
          </li>
        )}
        {sets.map((s) => (
          <li
            key={s.id}
            className="flex items-center justify-between gap-3 py-1.5 border-b border-dashed border-line/70 last:border-0"
            data-testid={`log-row-${s.id}`}
          >
            <div className="min-w-0 flex-1 text-ink-2">
              <span className="font-semibold text-ink">
                {s.reps ? `${s.reps} ` : ""}{group.title}
              </span>
              {s.weight ? <span className="text-ink"> | {formatStoredWeight(s.weight, s.weight_unit || "lb")}</span> : null}
              {s.time_seconds ? <span className="text-ink-3"> · {formatDuration(s.time_seconds)}</span> : null}
              {s.distance_meters ? <span className="text-ink-3"> · {formatStoredDistance(s.distance_meters, s.distance_unit || "m")}</span> : null}
              {s.height_inches ? <span className="text-ink-3"> · ↕{formatStoredHeight(s.height_inches, s.height_unit || "in")}</span> : null}
              {s.calories ? <span className="text-ink-3"> · {s.calories} cal</span> : null}
              {s.rpe ? <span className="text-ink-3"> · RPE {s.rpe}</span> : null}
              {s.notes ? <span className="text-ink-3 italic"> — {s.notes}</span> : null}
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              <EditLogDialog log={s} onSaved={onUpdated} />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onDelete(s.id)}
                className="rounded-lg h-7 w-7 text-error hover:bg-red-50 hover:text-error"
                data-testid={`delete-log-${s.id}`}
                aria-label="Delete this set"
              >
                <Trash size={12} />
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {summaryParts.length > 0 && (
        <div className="mt-3 ml-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sage/60 border border-sage">
          <span className="hs-label text-[10px] text-moss">Result</span>
          <span className="font-data text-xs text-moss-hover font-semibold">{summaryParts.join(" · ")}</span>
        </div>
      )}
    </div>
  );
}

function BlockGroup({ group, allLogs, onDelete, onUpdated }) {
  // Pull the rep scheme off either the parent block log or any of its child
  // movement logs (whichever the timer / coach saved). Shows the prescription
  // (e.g. "21-15-9 reps") right under the block title so the result tile
  // tells the full Ladder story.
  const repScheme = (group.sets.find((s) => Array.isArray(s.block_rep_scheme) && s.block_rep_scheme.length) || {}).block_rep_scheme;
  // Sibling movements logged for the same date+block_id (the child logs the
  // timer creates when a section is completed). Renders one line per movement
  // with name + weight where applicable.
  const dateKey = (group.sets[0]?.logged_at || "").slice(0, 10);
  const blockId = group.block_id || group.sets[0]?.block_id;
  const siblings = (allLogs || []).filter((l) => l.block_id && l.block_id === blockId
    && (l.logged_at || "").slice(0, 10) === dateKey
    && l.movement_name);
  // Dedupe by movement_name preserving order
  const seen = new Set();
  const movementLines = [];
  for (const s of siblings) {
    if (seen.has(s.movement_name)) continue;
    seen.add(s.movement_name);
    movementLines.push(s);
  }
  return (
    <div className="px-5 py-4 bg-muted2/30" data-testid={`history-block-${group.title}`}>
      <div className="flex items-start gap-2.5">
        <div className="h-8 w-8 rounded-lg bg-terracotta/15 grid place-items-center shrink-0">
          <Lightning size={16} weight="fill" className="text-terracotta" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.15em] bg-terracotta/10 text-terracotta">
              {group.block_type || "section"}
            </span>
            <h4 className="font-heading text-base font-semibold truncate">{group.title}</h4>
            {repScheme && repScheme.length > 0 && (
              <span
                className="px-2 py-0.5 rounded-md bg-terracotta text-white font-data text-[11px] font-semibold"
                data-testid={`history-block-scheme-${group.title}`}
              >
                {repScheme.join("-")} reps
              </span>
            )}
          </div>
          <div className="hs-label text-[10px] mt-1">{group.sets.length === 1 ? "Result" : "Attempts"}</div>
          {movementLines.length > 0 && (
            <ul className="mt-2 space-y-0.5 font-data text-[12px] text-ink-2" data-testid={`history-block-movements-${group.title}`}>
              {movementLines.map((m) => (
                <li key={m.id} className="flex items-baseline gap-1.5">
                  <span className="text-ink-3">·</span>
                  <span className="font-medium text-ink">{m.movement_name}</span>
                  {m.weight ? (
                    <span className="text-ink-3">@ {formatStoredWeight(m.weight, m.weight_unit || "lb")}</span>
                  ) : null}
                  {m.height_inches ? (
                    <span className="text-ink-3">↕{formatStoredHeight(m.height_inches, m.height_unit || "in")}</span>
                  ) : null}
                  {m.distance_meters ? (
                    <span className="text-ink-3">{formatStoredDistance(m.distance_meters, m.distance_unit || "m")}</span>
                  ) : null}
                  {m.calories ? <span className="text-ink-3">{m.calories} cal</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <ul className="mt-2 ml-10 space-y-1.5 font-data text-sm">
        {group.sets.map((s) => {
          const parts = [];
          if (s.total_time_seconds) parts.push(fmtTime(s.total_time_seconds));
          if (s.rounds_completed != null) parts.push(`${s.rounds_completed} rds`);
          if (s.interval_results?.length) parts.push(`${s.interval_results.length} intervals`);
          if (s.extra_reps != null) parts.push(`+${s.extra_reps} reps`);
          if (s.distance_meters) parts.push(formatStoredDistance(s.distance_meters, s.distance_unit || "m"));
          if (s.height_inches) parts.push(`↕${formatStoredHeight(s.height_inches, s.height_unit || "in")}`);
          if (s.calories) parts.push(`${s.calories} cal`);
          if (s.rpe) parts.push(`RPE ${s.rpe}`);

          // Compare with the most recent prior attempt of the same MetCon/AMRAP/etc.
          const prior = allLogs ? findPriorAttempt(allLogs, s) : null;
          let deltaChip = null;
          if (prior) {
            // Time-based comparison (MetCon RFT, Ladder, Tabata) — lower is better
            if (s.total_time_seconds && prior.total_time_seconds) {
              const diff = s.total_time_seconds - prior.total_time_seconds;
              if (diff !== 0) {
                const better = diff < 0;
                const mag = Math.abs(diff);
                const label = `${better ? "−" : "+"}${fmtTime(mag)} ${better ? "faster" : "slower"}`;
                deltaChip = (
                  <span
                    className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.12em] ${
                      better
                        ? "bg-moss text-white"
                        : "bg-error/10 text-error border border-error/30"
                    }`}
                    title={`Previous: ${fmtTime(prior.total_time_seconds)} on ${(prior.logged_at || "").slice(0, 10)}`}
                    data-testid={`delta-time-${s.id}`}
                  >
                    {label}
                  </span>
                );
              } else {
                deltaChip = (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.12em] bg-muted2 text-ink-3">
                    Tied prev
                  </span>
                );
              }
            } else if (s.rounds_completed != null && prior.rounds_completed != null) {
              // AMRAP-style: more rounds = better
              const curScore = (s.rounds_completed || 0) * 1000 + (s.extra_reps || 0);
              const priorScore = (prior.rounds_completed || 0) * 1000 + (prior.extra_reps || 0);
              const diff = curScore - priorScore;
              if (diff !== 0) {
                const better = diff > 0;
                const round_diff = (s.rounds_completed || 0) - (prior.rounds_completed || 0);
                const reps_diff = (s.extra_reps || 0) - (prior.extra_reps || 0);
                const label = `${better ? "+" : ""}${round_diff} rds${reps_diff ? ` ${reps_diff > 0 ? "+" : ""}${reps_diff} reps` : ""}`;
                deltaChip = (
                  <span
                    className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.12em] ${
                      better
                        ? "bg-moss text-white"
                        : "bg-error/10 text-error border border-error/30"
                    }`}
                    title={`Previous: ${prior.rounds_completed}+${prior.extra_reps || 0} on ${(prior.logged_at || "").slice(0, 10)}`}
                    data-testid={`delta-rounds-${s.id}`}
                  >
                    {label}
                  </span>
                );
              }
            }
          }

          return (
            <li
              key={s.id}
              className="flex items-center justify-between gap-3 py-1.5 border-b border-dashed border-line/70 last:border-0"
              data-testid={`log-row-${s.id}`}
            >
              <div className="min-w-0 flex-1 text-ink-2 flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-ink">{parts.join(" · ") || "—"}</span>
                {deltaChip}
                {s.notes ? <span className="text-ink-3 italic"> — {s.notes}</span> : null}
                {s.interval_results?.length ? (
                  <div className="w-full text-[11px] text-ink-3" data-testid={`log-splits-${s.id}`}>
                    Splits: {s.interval_results.map((r) => formatSplit(r, s.distance_unit || "m")).join(" / ")}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <EditLogDialog log={s} onSaved={onUpdated} />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onDelete(s.id)}
                  className="rounded-lg h-7 w-7 text-error hover:bg-red-50 hover:text-error"
                  data-testid={`delete-log-${s.id}`}
                  aria-label="Delete this attempt"
                >
                  <Trash size={12} />
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
